/**
 * SettingsPanel — tabbed overlay behind the ⚙ button.
 *
 *  Audio         — the browser audio check (passed in; it needs to know about
 *                  phone-bridge mode, which lives in DialerDevice)
 *  Dispositions  — add/remove the outcomes offered after a call (Supabase)
 *  Team          — theme + how sign-in works today
 *  Telephony     — the caller ID in use and how to change it
 *  Links         — quick jumps to the platforms this runs on
 */
import { useCallback, useEffect, useState } from 'react';

import { OUTCOMES } from '../lib/outcomes.js';
import TeamTab from './TeamTab.jsx';

const PLATFORM_LINKS = [
  { label: 'GitHub', href: 'https://github.com/automate305/parallel-dialer', icon: '⌥' },
  { label: 'Railway', href: 'https://railway.com', icon: '🚂' },
  { label: 'SignalWire dashboard', href: 'https://signalwire.com', icon: '📡' },
  { label: 'Supabase', href: 'https://supabase.com/dashboard', icon: '🗄' },
];

const TABS = [
  ['audio', 'Audio'], ['dispositions', 'Dispositions'], ['team', 'Team'], ['telephony', 'Telephony'], ['links', 'Links'],
];

export default function SettingsPanel({
  onClose, theme, onToggleTheme, apiBase, authHeaders, audioTab, bridgeMode, currentUser = null, accountsEnabled = false,
}) {
  const [tab, setTab] = useState('audio');
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | unavailable
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [outcomeKey, setOutcomeKey] = useState('meaningful');
  const [requiresNotes, setRequiresNotes] = useState(false);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch(`${apiBase}/api/dispositions`, { headers: authHeaders });
      if (res.status === 503) { setStatus('unavailable'); return; }
      if (!res.ok) throw new Error(`load failed (${res.status})`);
      setRows(await res.json());
      setStatus('ready');
    } catch (err) {
      setError(err.message);
      setStatus('ready');
    }
  }, [apiBase, authHeaders]);

  useEffect(() => {
    if (tab === 'dispositions') load();
  }, [tab, load]);

  async function add() {
    if (!label.trim()) return;
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/dispositions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ label: label.trim(), outcome_key: outcomeKey, requires_notes: requiresNotes }),
      });
      if (!res.ok) throw new Error(`save failed (${res.status})`);
      setLabel('');
      setRequiresNotes(false);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(id) {
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/dispositions/${id}`, { method: 'DELETE', headers: authHeaders });
      if (!res.ok) throw new Error(`remove failed (${res.status})`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  const callerId = import.meta.env.VITE_SIGNALWIRE_CALLER_ID || 'Not shown here';

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Settings">
      <div className="modal settings-modal">
        <div className="settings__head">
          <h2 className="settings__title">Settings</h2>
          <button type="button" className="btn btn--ghost" onClick={onClose} aria-label="Close settings">✕</button>
        </div>

        <div className="settings__tabs" role="tablist">
          {TABS.map(([key, text]) => (
            <button
              key={key} role="tab" type="button" aria-selected={tab === key}
              className={`settings__tab ${tab === key ? 'settings__tab--active' : ''}`}
              onClick={() => setTab(key)}
            >
              {text}
            </button>
          ))}
        </div>

        <div className="settings__body">
          {tab === 'audio' && <div className="settings__section">{audioTab}</div>}

          {tab === 'dispositions' && (
            <div className="settings__section">
              <h3 className="settings__section-title">Call outcomes</h3>
              <p className="settings__hint">
                These are the buttons in the post-call window. Each one counts as one of the dialer’s built-in outcomes — that
                is what drives HubSpot, reports and do-not-call suppression.
              </p>

              {status === 'loading' && <p className="settings__hint">Loading…</p>}
              {status === 'unavailable' && (
                <p className="settings__hint settings__hint--info">
                  Custom outcomes need Supabase (set <code>SUPABASE_URL</code> and <code>SUPABASE_SERVICE_KEY</code>). Until
                  then the built-in outcomes are used: {OUTCOMES.map((o) => o.label).join(', ')}.
                </p>
              )}
              {status === 'ready' && (
                <table className="sheet__table">
                  <thead><tr><th>Label</th><th>Counts as</th><th>Needs note</th><th></th></tr></thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.id}>
                        <td>{d.label}</td>
                        <td>{OUTCOMES.find((o) => o.key === d.outcome_key)?.label ?? d.outcome_key}</td>
                        <td>{d.requires_notes ? '✓' : '—'}</td>
                        <td><button type="button" className="btn btn--ghost btn--sm" onClick={() => remove(d.id)}>Remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {status !== 'unavailable' && (
                <div className="settings__add-row">
                  <input
                    className="input" placeholder="New outcome label" aria-label="New outcome label" maxLength={50}
                    value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
                  />
                  <select className="input" aria-label="Counts as" value={outcomeKey} onChange={(e) => setOutcomeKey(e.target.value)}>
                    {OUTCOMES.map((o) => <option key={o.key} value={o.key}>Counts as: {o.label}</option>)}
                  </select>
                  <label className="toggle">
                    <input type="checkbox" checked={requiresNotes} onChange={(e) => setRequiresNotes(e.target.checked)} />
                    Needs a note
                  </label>
                  <button type="button" className="btn btn--primary" onClick={add}>Add</button>
                </div>
              )}
              {error && <p className="settings__hint settings__hint--warn" role="alert">{error}</p>}
            </div>
          )}

          {tab === 'team' && (
            <TeamTab
              apiBase={apiBase} currentUser={currentUser} accountsEnabled={accountsEnabled}
              theme={theme} onToggleTheme={onToggleTheme}
            />
          )}

          {tab === 'telephony' && (
            <div className="settings__section">
              <h3 className="settings__section-title">Telephony</h3>
              <div className="settings__row"><span>Outbound caller ID</span><span className="mono">{callerId}</span></div>
              <div className="settings__row">
                <span>Agent audio</span>
                <span>{bridgeMode ? 'Phone bridge — calls ring your phone' : 'Browser softphone'}</span>
              </div>
              <div className="settings__hint settings__hint--info">
                To change the caller ID or add a number:
                <ol>
                  <li>Provision it in the SignalWire dashboard.</li>
                  <li>Update <code>SIGNALWIRE_CALLER_ID</code> in Railway Variables and redeploy.</li>
                  <li>Register it at <a href="https://freecallerregistry.com" target="_blank" rel="noopener noreferrer">freecallerregistry.com</a> so carriers don’t label it spam.</li>
                </ol>
                The caller ID shown above only appears if <code>VITE_SIGNALWIRE_CALLER_ID</code> was set at build time.
              </div>
            </div>
          )}

          {tab === 'links' && (
            <div className="settings__section">
              <h3 className="settings__section-title">Platform links</h3>
              <div className="links-grid">
                {PLATFORM_LINKS.map(({ label: text, href, icon }) => (
                  <a key={text} href={href} target="_blank" rel="noopener noreferrer" className="link-card">
                    <span aria-hidden="true">{icon}</span>
                    <span className="link-card__label">{text}</span>
                    <span className="link-card__arrow" aria-hidden="true">↗</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
